import { buttonVariants } from "./components/ui/button";
import { AccountQueryProvider } from "./account-query";
import { Link, Outlet, useLocation, useMatches } from "@tanstack/react-router";
import { useLayoutEffect, useRef } from "react";
import { EmptyState, LoadingBlock } from "./ui";
import { useTranslation } from "./i18n";
import { UserMenu } from "./components/ui/user-menu";
import { TopLoader } from "./components/ui/top-loader";
import { Brand } from "./components/ui/brand";
import { useSession, useAdminAccess, useSignOut } from "./route-shared";

import { CreativeHomePage } from "./components/home/creative-home-page";
import { HomeAnnouncement } from "./components/home/home-announcement";
import { ShowcaseMediaProvider } from "./components/showcase-media-provider";
import { darkSurfaceClasses, SurfaceThemeProvider } from "./components/ui/surface-theme";
import { cn } from "./lib/utils";
import { SlidingNavigation } from "./components/ui/sliding-navigation";

export function AppShell() {
  const { t } = useTranslation();
  const sessionQuery = useSession();
  const session = sessionQuery.data?.session ? sessionQuery.data : null;
  const adminQuery = useAdminAccess(session?.user?.id);
  const signOut = useSignOut();
  const locationPathname = useLocation({ select: location => location.pathname });
  // The URL can advance before the router commits its next rendered page.
  const matchedPathname = useMatches({ select: matches => {
    const leaf = matches.at(-1);
    return leaf?.status === "notFound" ? undefined : leaf?.pathname;
  } });
  const pathname = matchedPathname ?? locationPathname;
  const isHome = pathname === "/";
  const isSandbox = pathname === "/sandbox";
  const outlet = <Outlet key={session?.user?.id ?? "anonymous"} />;
  const sharedHeader = ["/", "/sandbox", "/explore", "/requests", "/login", "/about-nexra", "/contact", "/docs"].includes(pathname) || pathname.startsWith("/models/") || pathname.startsWith("/docs/");
  const shellRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const shell = shellRef.current;
    if (!shell || !sharedHeader) return;
    const header = shell.querySelector<HTMLElement>(":scope > .topbar");
    const announcement = shell.querySelector<HTMLElement>(":scope > .home-announcement");
    if (!header) return;
    // Measure the navigation independently; only the homepage has an announcement.
    const measure = () => {
      const announcementHeight = announcement?.getBoundingClientRect().height ?? 0;
      shell.style.setProperty("--site-announcement-height", `${announcementHeight}px`);
      shell.style.setProperty("--site-header-height", `${header.getBoundingClientRect().height + announcementHeight}px`);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    if (announcement) observer.observe(announcement);
    measure();
    return () => { observer.disconnect(); shell.style.removeProperty("--site-header-height"); shell.style.removeProperty("--site-announcement-height"); };
  }, [sharedHeader, isHome, isSandbox]);
  const navigation = <>
    <Link to="/" activeOptions={{ exact: true }}>{t("nav.home")}</Link>
    <Link to="/sandbox">{t(sharedHeader ? "creativeHome.playground" : "nav.playground")}</Link>
    <Link to="/explore" data-nav-current={pathname.startsWith("/models/") ? "true" : undefined}>{t(sharedHeader ? "creativeHome.modelSquare" : "nav.models")}</Link>
    <Link to="/requests">{t("nav.requests")}</Link>
    <Link to="/docs">{t("nav.documentation")}</Link>
  </>;

  return (
    <SurfaceThemeProvider appearance={isSandbox ? "dark" : isHome ? "light" : "default"}>
    <div ref={shellRef} data-immersive={isSandbox || undefined} className={`app-shell
  [min-height:100vh]
${isHome ? ` light-home-shell
  [--background:#fff] [--foreground:#202523] [--muted:#f0efeb] [--muted-foreground:#626963] [--primary:#293d31] [--primary-foreground:#fff] [--accent:#dfebe4] [--accent-foreground:#28675f] [--border:#d8dad5] [--input:#b6c0b9] [--ring:#28675f] [--home-gutter:32px] [background:var(--background)] text-foreground [color-scheme:light]
  maxw-[480px]:[--home-gutter:16px]
` : ""}${sharedHeader ? ` light-navigation-shell
  [--site-navigation-height:calc(var(--site-header-height,80px)_-_var(--site-announcement-height,0px))]
  [&>.topbar]:h-20 [&>.topbar]:min-h-0 [&>.topbar]:grid [&>.topbar]:[grid-template-columns:minmax(0,1fr)_auto_minmax(0,1fr)] [&>.topbar]:gap-6 [&>.topbar]:[padding:0_32px] [&>.topbar]:[background:var(--navigation-background,#ffffffe0)] [&>.topbar]:border-b [&>.topbar]:border-border/50 [&>.topbar]:sticky [&>.topbar]:top-0 [&>.topbar]:[z-index:30]
  [&[data-immersive]>.topbar]:fixed [&[data-immersive]>.topbar]:inset-x-0 [&[data-immersive]>.topbar]:top-[var(--site-announcement-height,0px)]
  minw-[601px]:[&[data-immersive]>.topbar]:h-18
  maxw-[600px]:[&[data-immersive]>.topbar]:py-2 maxw-[600px]:[&[data-immersive]>.topbar]:gap-y-1
  [&>.topbar>a]:justify-self-start
  [&_.sliding-navigation]:justify-self-center
  [&_.topbar-actions]:ml-0 [&_.topbar-actions]:justify-self-end
  maxw-[960px]:[&>.topbar]:gap-4
  maxw-[1100px]:[&>.topbar]:[grid-template-columns:auto_minmax(0,1fr)_auto]
  maxw-[600px]:[&>.topbar]:h-auto maxw-[600px]:[&>.topbar]:py-4 maxw-[600px]:[&>.topbar]:grid maxw-[600px]:[&>.topbar]:[grid-template-columns:1fr_auto] maxw-[600px]:[&>.topbar]:gap-y-3.5
  maxw-[600px]:[&_.sliding-navigation]:[grid-column:1/-1] maxw-[600px]:[&_.sliding-navigation]:[grid-row:2]
  maxw-[600px]:[&_.topbar-actions]:[grid-column:2] maxw-[600px]:[&_.topbar-actions]:[grid-row:1]
  maxw-[480px]:[&>.topbar]:px-4
` : ""}`}>
      <TopLoader />
      {isHome ? <HomeAnnouncement /> : null}
      <header className={cn(`topbar
  items-center [background:rgba(255,255,255,.92)] [border-bottom:1px_solid_var(--line)] grid gap-6 [grid-template-columns:auto_minmax(0,_1fr)_auto] h-14.5 [padding:0_32px] sticky top-0 [z-index:30] [backdrop-filter:blur(16px)]
  maxw-[720px]:gap-2.5 maxw-[720px]:[grid-template-columns:auto_minmax(0,_1fr)_auto] maxw-[720px]:[padding:0_16px]
`, isSandbox && `${darkSurfaceClasses} [--navigation-background:#24272bad]`)}>
        <Link className={cn("inline-flex shrink-0 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring", isSandbox && "[&_img]:brightness-0 [&_img]:invert max-[600px]:[&_img]:size-9 max-[600px]:[&>span>span]:text-2xl")} to="/" aria-label={t("nav.homeAria")}>
          <Brand size={sharedHeader ? "lg" : "default"} />
        </Link>
        {sharedHeader ? <SlidingNavigation label={t("nav.mainAria")}>{navigation}</SlidingNavigation> :         <nav className="main-nav flex min-w-0 items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>a]:inline-flex [&>a]:h-9 [&>a]:shrink-0 [&>a]:items-center [&>a]:whitespace-nowrap [&>a]:rounded-md [&>a]:px-3 [&>a]:text-sm [&>a]:font-medium [&>a]:text-muted-foreground [&>a]:outline-none [&>a:hover]:text-foreground [&>a:focus-visible]:ring-2 [&>a:focus-visible]:ring-inset [&>a:focus-visible]:ring-ring [&>a[data-status=active]]:font-semibold [&>a[data-status=active]]:text-foreground max-sm:[&>a]:px-2 max-sm:[&>a]:text-xs" aria-label={t("nav.mainAria")}>{navigation}</nav>}
        <div className={`topbar-actions
  items-center flex gap-3
`}>
          <SurfaceThemeProvider appearance={isSandbox ? "dark" : "light"}>
            <UserMenu pending={sessionQuery.isLoading || signOut.isPending} isAdmin={!signOut.isPending && adminQuery.data === true} session={session} onSignOut={() => signOut.mutate()} />
          </SurfaceThemeProvider>
        </div>
      </header>
      <main className="main-content">{signOut.isPending ? <LoadingBlock /> : <AccountQueryProvider userId={session?.user?.id}>{isHome || pathname === "/sandbox" ? <ShowcaseMediaProvider>{outlet}</ShowcaseMediaProvider> : outlet}</AccountQueryProvider>}</main>
    </div>
    </SurfaceThemeProvider>
  );
}

export function DashboardPage() {
  return <CreativeHomePage />;
}

export { LoginPage } from "./login-page";

export function NotFoundPage() {
  const { t } = useTranslation();
  return <div className="page"><EmptyState title={t("notFound.title")} detail={t("notFound.detail")} /><div className="center-actions"><Link className={buttonVariants({ variant: "default", className: "" })} to="/">{t("notFound.backHome")}</Link></div></div>;
}
